
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download, Eye, Heart, MessageSquare, Share2, ShoppingCart } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";

interface AnalyticsData {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  sales: number;
}

interface AnalyticsDashboardProps {
  data: AnalyticsData[];
  contentType: 'pin' | 'webtoon' | 'webnovel';
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
  isLoading: boolean;
}

export const AnalyticsDashboard = ({ data, contentType, onDateRangeChange, isLoading }: AnalyticsDashboardProps) => {
  const [timeRange, setTimeRange] = useState<'7days' | '30days' | 'month' | 'custom'>('7days');
  
  const handleTimeRangeChange = (value: string) => {
    const today = new Date();
    let startDate: Date;
    let endDate = today;
    
    switch (value) {
      case '7days':
        startDate = subDays(today, 7);
        break;
      case '30days':
        startDate = subDays(today, 30);
        break;
      case 'month':
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
        break;
      default:
        startDate = subDays(today, 7);
    }
    
    setTimeRange(value as any);
    onDateRangeChange(startDate, endDate);
  };

  // Calculate summary metrics
  const totalViews = data.reduce((sum, item) => sum + item.views, 0);
  const totalLikes = data.reduce((sum, item) => sum + item.likes, 0);
  const totalComments = data.reduce((sum, item) => sum + item.comments, 0);
  const totalShares = data.reduce((sum, item) => sum + item.shares, 0);
  const totalSales = data.reduce((sum, item) => sum + item.sales, 0);

  const contentTypeName = {
    pin: 'Pin',
    webtoon: 'Webtoon',
    webnovel: 'Webnovel'
  }[contentType];

  const exportData = () => {
    // Create CSV content
    const headers = "Data,Visualizações,Curtidas,Comentários,Compartilhamentos,Vendas\n";
    const rows = data.map(item => 
      `${item.date},${item.views},${item.likes},${item.comments},${item.shares},${item.sales}`
    ).join('\n');
    
    const csvContent = "data:text/csv;charset=utf-8," + headers + rows;
    const encodedUri = encodeURI(csvContent);
    
    // Create a link and trigger the download
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${contentTypeName.toLowerCase()}_analytics_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-xl font-semibold">Análise de Desempenho</h2>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecionar período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Últimos 7 dias</SelectItem>
              <SelectItem value="30days">Últimos 30 dias</SelectItem>
              <SelectItem value="month">Este mês</SelectItem>
              <SelectItem value="lastMonth">Mês anterior</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={exportData}>
            <Download className="mr-2 h-4 w-4" />
            Exportar
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Visualizações</CardDescription>
            <CardTitle className="text-2xl flex items-center">
              <Eye className="mr-2 h-4 w-4 text-muted-foreground" />
              {totalViews}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Curtidas</CardDescription>
            <CardTitle className="text-2xl flex items-center">
              <Heart className="mr-2 h-4 w-4 text-muted-foreground" />
              {totalLikes}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Comentários</CardDescription>
            <CardTitle className="text-2xl flex items-center">
              <MessageSquare className="mr-2 h-4 w-4 text-muted-foreground" />
              {totalComments}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Compartilhamentos</CardDescription>
            <CardTitle className="text-2xl flex items-center">
              <Share2 className="mr-2 h-4 w-4 text-muted-foreground" />
              {totalShares}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Vendas</CardDescription>
            <CardTitle className="text-2xl flex items-center">
              <ShoppingCart className="mr-2 h-4 w-4 text-muted-foreground" />
              {totalSales}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
      
      <Tabs defaultValue="views">
        <TabsList className="mb-4">
          <TabsTrigger value="views">Visualizações</TabsTrigger>
          <TabsTrigger value="engagement">Interações</TabsTrigger>
          <TabsTrigger value="sales">Vendas</TabsTrigger>
        </TabsList>
        
        <TabsContent value="views" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Visualizações ao longo do tempo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <p>Carregando dados...</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="views" stroke="#8884d8" name="Visualizações" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="engagement" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Interações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <p>Carregando dados...</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="likes" stroke="#8884d8" name="Curtidas" />
                      <Line type="monotone" dataKey="comments" stroke="#82ca9d" name="Comentários" />
                      <Line type="monotone" dataKey="shares" stroke="#ffc658" name="Compartilhamentos" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="sales" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <p>Carregando dados...</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="sales" fill="#8884d8" name="Vendas" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
